/**
 * Generic, surface-keyed Magnus session-token store for embedded copilots.
 *
 * Cross-site iframe copilots (Procore today) can't rely on a first-party
 * session cookie, so after a login popup mints a raw session token we persist
 * it in `sessionStorage` and inject it as an `Authorization: Bearer` header on
 * every outgoing fetch. The storage key per surface comes from the embedded
 * host registry metadata (`sessionStorageKeyForSurface`), so a new copilot
 * gets token storage + header injection just by declaring a `sessionStorageKey`
 * — no new module.
 *
 * The Office add-ins are intentionally NOT served by this store: they manage
 * their session bespoke (MSAL + a dev-only cookie mirror); see
 * `office-addin-session.ts`. Both are merged in `embedded-session.ts`.
 */

import {
  embeddedHostMeta,
  sessionStorageKeyForSurface,
} from "./embedded-hosts";
import type { AppSurface } from "./host";

/** Read the stored session token for a surface (`null` when absent). */
export function getHostSessionToken(surface: AppSurface): string | null {
  const key = sessionStorageKeyForSurface(surface);
  if (!key) {
    return null;
  }
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Persist the session token minted for a surface. No-op if storage is unavailable. */
export function setHostSessionToken(surface: AppSurface, token: string): void {
  const key = sessionStorageKeyForSurface(surface);
  if (!key) {
    return;
  }
  try {
    sessionStorage.setItem(key, token);
  } catch {
    // sessionStorage unavailable (private mode); the cookie set by the
    // surface's session route still carries the session.
  }
}

/** Remove a surface's stored session token (e.g. on sign-out / a dead-token 401). */
export function clearHostSessionToken(surface: AppSurface): void {
  const key = sessionStorageKeyForSurface(surface);
  if (!key) {
    return;
  }
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * Bearer header for an embedded surface's session token, merged into outgoing
 * fetches by `getEmbeddedSessionHeaders`. Empty on standalone surfaces and on
 * hosts (Office) that don't use the generic store.
 *
 * Surface-agnostic by design: we scan every descriptor that declares a
 * `sessionStorageKey` and use the first stored token, rather than keying off
 * `getClientHost()`. In a cross-site iframe the host marker and the captured
 * token can land in different orders, so gating the header on the marker would
 * drop the bearer even though a valid token exists. A client is only ever in
 * one embedded host, so at most one of these keys is populated.
 */
export function getHostSessionHeaders(): Record<string, string> {
  try {
    for (const meta of embeddedHostMeta) {
      if (!meta.sessionStorageKey) {
        continue;
      }
      const token = sessionStorage.getItem(meta.sessionStorageKey);
      if (token) {
        return { Authorization: `Bearer ${token}` };
      }
    }
  } catch {
    // sessionStorage unavailable (private mode / partitioned iframe).
  }
  return {};
}
